import { AsyncPipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { Router, RouterOutlet } from '@angular/router';
import { RootState } from '@next/core-lib/app';
import { PageDetailComponent } from '@next/core-lib/components';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { RouterSelectors } from '@next/core-lib/router-store';
import { AgrUser, UserSelectors } from '@next/core-lib/user';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, switchMap, take, withLatestFrom } from 'rxjs/operators';

import { Calculation } from '../../models/feed-profit.model';
import * as actions from '../../store/feed-profit.actions';
import { getIsChanged, getSelectedCalculation } from '../../store/feed-profit.selectors';
import { FeedProfitNavigationComponent } from '../feed-profit-navigation/feed-profit-navigation.component';
import { SettingsDialogComponent } from '../settings-dialog/settings-dialog.component';

@Component({
  selector: 'app-feed-profit-item',
  templateUrl: './feed-profit-item.component.html',
  imports: [
    PageDetailComponent,
    MatButton,
    MatIcon,
    FeedProfitNavigationComponent,
    RouterOutlet,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class FeedProfitItemComponent implements OnInit {
  pageTitle$: Observable<string> = this.store
    .select(RouterSelectors.getPath)
    .pipe(
      map((route: string) =>
        this.localeService.translate(
          `feed-profit.detail.${route.indexOf('/create') !== -1 ? 'create' : 'edit'}-title`,
        ),
      ),
    );

  routeUrl$: Observable<string> = this.store.pipe(select(RouterSelectors.getPath));

  isQuickSimulation$: Observable<boolean> = this.routeUrl$.pipe(
    map((url) => url.indexOf('quick-simulation') > 0),
  );
  isExtendedSimulation$: Observable<boolean> = this.routeUrl$.pipe(
    map((url) => url.indexOf('extended-simulation') > 0),
  );

  isBaseline$: Observable<boolean> = this.isQuickSimulation$.pipe(
    withLatestFrom(this.isExtendedSimulation$),
    switchMap(([isQuickSimulation, isExtendedSimulation]) => [
      !(isQuickSimulation || isExtendedSimulation),
    ]),
  );

  calculation: Calculation;

  constructor(
    private readonly store: Store<RootState>,
    private readonly localeService: LocaleService,
    private readonly router: Router,
    private readonly dialog: DialogService,
  ) {}
  ngOnInit(): void {
    this.store
      .select(getSelectedCalculation)
      .pipe(take(1))
      .subscribe((calculation) => {
        if (!calculation) {
          this.router.navigate(['/feed-profit']).then();
          return;
        }
        this.calculation = calculation;
      });
  }

  safeBack() {
    this.store
      .select(getSelectedCalculation)
      .pipe(take(1), withLatestFrom(this.store.select(getIsChanged)))
      .subscribe(([_calculation, isChanged]) => {
        if (!isChanged) {
          this.back();
        } else {
          this.dialog
            .pendingChanges({
              title: this.localeService.translate(
                'feed-profit.detail.extended-simulation.item-cancellation-title',
              ),
              subTitle: this.localeService.translate(
                'feed-profit.detail.extended-simulation.item-cancellation-subtitle',
              ),
              pushHistoryState: false,
            })
            .subscribe((result) => {
              if (result.status === DIALOG_RETURN_TYPES.confirm) {
                this.back();
              }
            });
        }
      });
  }

  back = () => this.router.navigate(['/feed-profit']);

  openSettingsDialog() {
    this.store
      .select(UserSelectors.selectUser)
      .pipe(take(1))
      .subscribe((user: AgrUser) => {
        this.dialog
          .fullscreenDialog(
            SettingsDialogComponent,
            {
              title: this.localeService.translate('feed-profit.settings.title'),
              subTitle: this.localeService.translate('feed-profit.settings.subtitle'),
              confirmButton: true,
              confirmButtonText: this.localeService.translate('save'),
              calculation: this.calculation,
              fullscreen: true,
            },
            {
              maxWidth: '70vw',
              maxHeight: '100vh',
              height: '100%',
              width: '70%',
            },
          )
          .subscribe((result) => {
            if (result.status === DIALOG_RETURN_TYPES.confirm) {
              const newCalculation = {
                ...this.calculation,
                name: result.result.calculation.name,
                genders: result.result.calculation.genders,
                country: result.result.calculation.country,
                feeders: result.result.calculation.feeders,
                calculatedFeeder: result.result.calculation.calculatedFeeder,
                createdBy: this.calculation.createdBy || user.name,
                createdAt: this.calculation.createdAt || new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                companyDetails: result.result.calculation.companyDetails,
                baseLine: result.result.calculation.baseLine,
                extendedBaseLine: result.result.calculation.extendedBaseLine,
                comparisonSimulation: result.result.calculation.comparisonSimulation,
                extendedComparisonSimulation:
                  result.result.calculation.extendedComparisonSimulation,
              };
              this.store.dispatch(
                actions.upsertCalculationRequest({
                  calculation: newCalculation,
                }),
              );
            }
          });
      });
  }
}
